import * as React from "react";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Search,
  ChevronDown,
  Check,
  X,
  Loader2,
  FileText,
  Filter,
  RefreshCw,
  ArrowUpDown,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  useInfiniteFilterForms,
  FormSortOption,
} from "@/hooks/useLocalStorage";
import {
  FormFilterItem,
  parseFormFilterResults,
} from "@/lib/api-services";
import { cn } from "@/lib/utils";

export const SORT_OPTIONS: {
  label: string;
  value: FormSortOption;
  description: string;
}[] = [
  { label: "Standard", value: "Standard", description: "Default order" },
  { label: "Alphabetical", value: "Lex", description: "A to Z" },
  { label: "By IDs", value: "Id_based", description: "Ordered by Form ID" },
];

export interface FilterTypeSearchProps<T = any> {
  value?: string;
  onChange: (val: string, item?: FormFilterItem) => void;
  formType?: string;
  placeholder?: string;
  width?: string | number;
  className?: string;
  disabled?: boolean;
  defaultSort?: FormSortOption;

  // Optional legacy props for compatibility
  filterTypeData?: T[];
  isLoading?: boolean;
  onSearch?: (query: string) => void;
}

export function FilterTypeSearch<T = any>({
  value = "",
  onChange,
  formType = "Portal",
  placeholder = "Select Form Name",
  width,
  className,
  disabled = false,
  defaultSort = "Lex",
  filterTypeData,
  isLoading: legacyLoading,
  onSearch: legacyOnSearch,
}: FilterTypeSearchProps<T>) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [sortBy, setSortBy] = useState<FormSortOption>(defaultSort);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Debounce search query by 300ms
  useEffect(() => {
    const handler = setTimeout(() => {
      const q = searchInput.trim();
      setDebouncedQuery(q);
      if (legacyOnSearch) {
        legacyOnSearch(q);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, legacyOnSearch]);

  // Infinite query for form filter search (scroll pagination & dynamic sort)
  const resolvedType = formType === "PortalForm" ? "Portal" : (formType || "Portal");
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isQueryLoading,
    isError,
    refetch,
  } = useInfiniteFilterForms(debouncedQuery, sortBy, resolvedType, open);

  const isLoading = isQueryLoading || legacyLoading;

  // Auto-focus input when opened
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setSearchInput("");
      setDebouncedQuery("");
    }
  }, [open]);

  // Flatten and deduplicate items
  const allForms = useMemo(() => {
    if (data?.pages && data.pages.length > 0) {
      const seen = new Set<string | number>();
      const list: FormFilterItem[] = [];

      for (const page of data.pages) {
        const { items } = parseFormFilterResults(page.data);
        for (const item of items) {
          if (!seen.has(item.id)) {
            seen.add(item.id);
            list.push(item);
          }
        }
      }
      return list;
    }

    // Fallback to legacy filterTypeData if query data not present
    if (filterTypeData && Array.isArray(filterTypeData)) {
      return filterTypeData.map((item: any, i: number) => ({
        id: item?.Id || item?.FormId || item?.ID || i,
        name: item?.FormName || item?.Name || item?.name || item?.Label || item?.Title || "",
        code: item?.FormCode || item?.Code,
        type: item?.FormType || item?.Type,
        raw: item,
      }));
    }

    return [];
  }, [data, filterTypeData]);

  // Handle scroll pagination near the bottom
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop <= clientHeight + 60) {
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  // IntersectionObserver for bottom sentinel
  useEffect(() => {
    if (!open || !hasNextPage || isFetchingNextPage) return;

    const sentinel = sentinelRef.current;
    const container = listRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: container,
        rootMargin: "80px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSelectForm = (item: FormFilterItem) => {
    onChange(item.name, item);
    setOpen(false);
  };

  const handleSelectAll = () => {
    onChange("");
    setOpen(false);
  };

  const handleSelectCustomQuery = () => {
    if (debouncedQuery) {
      onChange(debouncedQuery);
      setOpen(false);
    }
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const isExactMatchPresent = useMemo(() => {
    if (!debouncedQuery) return false;
    const q = debouncedQuery.toLowerCase();
    return allForms.some((f) => f.name.toLowerCase() === q);
  }, [debouncedQuery, allForms]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          style={width ? { width } : undefined}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border-black-800 border-2 bg-background px-3 py-2 text-sm ring-offset-background",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50 transition-colors text-left",
            className
          )}
        >
          <div className="flex items-center min-w-0 flex-1 mr-2">
            {value ? (
              <span className="truncate font-medium text-foreground flex items-center">
                <FileText className="h-3.5 w-3.5 mr-1.5 text-workfinity-600 shrink-0" />
                <span className="truncate">{value}</span>
              </span>
            ) : (
              <span className="text-muted-foreground truncate">{placeholder}</span>
            )}
          </div>

          <div className="flex items-center space-x-1 shrink-0">
            {value && !disabled && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClearSelection}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleClearSelection(e as any);
                  }
                }}
                className="rounded-full p-0.5 hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                title="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 opacity-60 transition-transform duration-200",
                open && "transform rotate-180"
              )}
            />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={4}
        style={width ? { width } : undefined}
        className="w-[var(--radix-popover-trigger-width)] min-w-[300px] max-w-[480px] p-0 shadow-lg border border-border bg-popover rounded-md overflow-hidden z-50"
      >
        {/* Search Input Bar */}
        <div className="flex items-center px-3 py-2 border-b border-border bg-muted/30">
          <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search form name..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground text-foreground"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="text-muted-foreground hover:text-foreground p-0.5"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {isLoading && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500 ml-1.5 shrink-0" />
          )}
        </div>

        {/* Order By (Sort) Toolbar */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-muted/40 border-b border-border/60 text-xs">
          <span className="flex items-center text-[11px] font-medium text-muted-foreground shrink-0 mr-2">
            <ArrowUpDown className="h-3 w-3 mr-1 text-muted-foreground/70" />
            Order by:
          </span>
          <div className="flex items-center space-x-1 overflow-x-auto py-0.5">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSortBy(opt.value)}
                title={opt.description}
                className={cn(
                  "px-2 py-0.5 rounded text-[11px] font-medium transition-all whitespace-nowrap",
                  sortBy === opt.value
                    ? "bg-workfinity-500 text-white shadow-xs font-semibold"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action Options */}
        <div className="border-b border-border/60 bg-muted/10 p-1">
          {/* Option: All Forms */}
          <button
            type="button"
            onClick={handleSelectAll}
            className={cn(
              "w-full flex items-center justify-between px-2.5 py-1.5 text-xs rounded-sm transition-colors text-left font-medium",
              !value
                ? "bg-workfinity-50 text-workfinity-700 font-semibold"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            <span>All forms</span>
            {!value && <Check className="h-3.5 w-3.5 text-workfinity-600" />}
          </button>

          {/* Option: Filter by custom typed substring */}
          {debouncedQuery && !isExactMatchPresent && (
            <button
              type="button"
              onClick={handleSelectCustomQuery}
              className="w-full flex items-center px-2.5 py-1.5 text-xs rounded-sm transition-colors text-left text-workfinity-700 hover:bg-workfinity-50 font-medium"
            >
              <Filter className="h-3 w-3 mr-1.5 shrink-0" />
              <span className="truncate">
                Filter as &ldquo;<span className="underline">{debouncedQuery}</span>&rdquo;
              </span>
            </button>
          )}
        </div>

        {/* Scrollable Forms List */}
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="max-h-[260px] overflow-y-auto p-1 divide-y divide-border/20 text-sm"
        >
          {/* Loading Initial State */}
          {isLoading && allForms.length === 0 && (
            <div className="py-6 text-center text-muted-foreground space-y-2">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-workfinity-500" />
              <p className="text-xs">Searching forms...</p>
            </div>
          )}

          {/* Error State */}
          {isError && allForms.length === 0 && (
            <div className="py-6 text-center text-red-500 space-y-2">
              <p className="text-xs">Failed to load forms.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                className="h-7 text-xs"
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Retry
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !isError && allForms.length === 0 && (
            <div className="py-6 text-center text-muted-foreground">
              <p className="text-xs">No forms found matching &ldquo;{debouncedQuery}&rdquo;</p>
            </div>
          )}

          {/* Form Items */}
          {allForms.map((item) => {
            const isSelected = value.toLowerCase() === item.name.toLowerCase();
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSelectForm(item)}
                className={cn(
                  "w-full flex items-center justify-between px-2.5 py-2 rounded-sm text-left transition-colors group",
                  isSelected
                    ? "bg-workfinity-50 text-workfinity-800 font-medium"
                    : "hover:bg-muted text-foreground"
                )}
              >
                <div className="flex flex-col min-w-0 mr-2">
                  <div className="flex items-center space-x-1.5">
                    <span className="truncate font-medium">{item.name}</span>
                    {item.code && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-muted text-muted-foreground shrink-0 border border-border/40 font-mono">
                        {item.code}
                      </span>
                    )}
                    {item.type && (
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-workfinity-50 text-workfinity-700 shrink-0 border border-workfinity-200">
                        {item.type}
                      </span>
                    )}
                  </div>
                </div>

                {isSelected && (
                  <Check className="h-4 w-4 text-workfinity-600 shrink-0 ml-1" />
                )}
              </button>
            );
          })}

          {/* Bottom Pagination Indicator */}
          {isFetchingNextPage && (
            <div className="py-2.5 flex items-center justify-center space-x-2 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-workfinity-500" />
              <span className="text-xs">Loading more forms...</span>
            </div>
          )}

          {/* All items loaded indicator */}
          {!hasNextPage && allForms.length >= 20 && !isLoading && (
            <div className="py-2 text-center text-[11px] text-muted-foreground/60 border-t border-border/30 mt-1">
              Showing all {allForms.length} forms
            </div>
          )}

          {/* Sentinel for IntersectionObserver */}
          <div ref={sentinelRef} className="h-1 w-full pointer-events-none" />
        </div>
      </PopoverContent>
    </Popover>
  );
}
